import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createContext, useContext, useMemo } from 'react';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { ADS_AUDIENCE_SLOTS_MAX, AdsAudienceSlotName, LATIN_ONLY_FIELD_MESSAGE, hasCyrillic, } from '@wit-support/admin-contracts';
import { Button, ErrorCard, InlineNotice, SectionCard, Skeleton, formatNumberRu, toast, } from '@wit-support/ui';
import { Actions, SubmitButton, ZodField, ZodForm } from '@wit-support/ui-forms';
import { EntityAvatar, EntityKindIcon, useBreadcrumbDetail } from '@wit-support/ui-domain';
import { RefreshCw } from 'lucide-react';
import { adsApiUrl, getAccount, putAccountProfile } from '../api.js';
import { ADS_QUERY_DEFAULTS, REFERENCE_STALE_MS } from '../query-policy.js';
import { useAdsLauncherPerms } from '../perms.js';
import { ADS_KEY, AdsScreen, accountKey, describeAdsError, submitting, useAdsHealth, useRegistry, } from '../shared.js';
import { notReadyText } from '../components/account-chips.js';
import { useRegistryIndex } from '../registry/use-registry.js';
import { AdsEntity, entityRef, findLabel, useEntityLabels } from '../entity-labels.js';
const NONE = '__none__';
const STALE_AUDIENCE_MESSAGE = 'Этой аудитории нет в кабинете (удалена или недоступна) — выберите другую или «Не выбрана»';
const META_ID = /^\d+$/;
/** Текст, который уходит в Meta: только латиница (та же проверка, что у admin-api и MCP). */
const latinOnly = (s) => s.refine((v) => !hasCyrillic(v), { message: LATIN_ONLY_FIELD_MESSAGE });
/** Поле id из ассетов кабинета: выбор из списка, если он есть; иначе ввод цифрами. */
function idField(options, label, required, description) {
    const meta = { label, ...(description ? { description } : {}) };
    if (options.length > 0) {
        const values = required ? options.map((o) => o.value) : [NONE, ...options.map((o) => o.value)];
        const opts = required ? options : [{ value: NONE, label: 'Не задано' }, ...options];
        const s = z.enum(values, { message: `${label}: выберите из списка` });
        return (required ? s : s.optional()).meta({ ...meta, options: opts, placeholder: 'Выберите…' });
    }
    const s = z.string().trim().regex(META_ID, { message: 'Id Meta — только цифры' });
    return (required ? s : s.or(z.literal('')).optional()).meta(meta);
}
/** Подпись варианта: имя (Instagram — «имя · @ник»); без имени — ник, потом вид. */
function assetOptionLabel(kind, x) {
    const nick = x.username?.trim().replace(/^@/, '');
    const name = x.name?.trim();
    if (name)
        return kind === 'instagram' && nick ? `${name} · @${nick}` : name;
    if (nick)
        return `@${nick}`;
    return { page: 'Страница', instagram: 'Instagram', pixel: 'Пиксель' }[kind];
}
const AssetLabelsContext = createContext(() => undefined);
/** Картинка варианта: аватар страницы / Instagram (инициалы, пока нет), у пикселя — значок вида. */
function AssetOptionIcon({ kind, asset }) {
    const labelOf = useContext(AssetLabelsContext);
    if (kind === 'pixel')
        return _jsx(EntityKindIcon, { kind: "pixel" });
    const label = labelOf(kind, asset.id);
    return (_jsx(EntityAvatar, { kind: kind, name: asset.name ?? label?.name ?? null, username: asset.username ?? label?.username ?? null, src: label?.avatarUrl ? adsApiUrl(label.avatarUrl) : null }));
}
const named = (kind, list) => list.map((x) => ({
    value: x.id,
    label: assetOptionLabel(kind, x),
    icon: _jsx(AssetOptionIcon, { kind: kind, asset: x }),
}));
/** Подсказка поля «Домен конверсий» (v4 §11.8). */
export const CONVERSION_DOMAIN_HINT = 'Например, witlab.top. Пусто — объявления создаются без домена конверсий (так создана большая часть живых объявлений).';
export function AccountPage() {
    const { id } = useParams({ strict: false });
    return (_jsx(AdsScreen, { title: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430", width: "narrow", children: _jsx(AccountBody, { id: id }) }));
}
function AccountBody({ id }) {
    const qc = useQueryClient();
    // карточка кабинета тянет из Meta страницы/аудитории — справочник, 5 мин;
    // свои правки сбрасывают кэш (`ADS_KEY`)
    const q = useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: accountKey(id),
        queryFn: () => getAccount(id),
        staleTime: REFERENCE_STALE_MS,
    });
    // Реестр — для подписей полей профиля (готовность, поля по `enabled`): форма
    // монтируется, когда он пришёл.
    const registry = useRegistry();
    const refresh = useMutation({
        mutationFn: () => getAccount(id, true),
        onSuccess: (d) => {
            qc.setQueryData(accountKey(id), d);
            toast.success('Данные кабинета перечитаны из Meta');
        },
        onError: (err) => toast.error(describeAdsError(err)),
    });
    useBreadcrumbDetail(q.data?.name ?? null);
    const err = q.error ?? registry.error;
    if (err)
        return _jsx(ErrorCard, { children: describeAdsError(err) });
    if (!q.data || !registry.data)
        return _jsx(Skeleton, { className: "h-96 w-full" });
    return (_jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs("div", { className: "flex flex-wrap items-center gap-2 text-sm", children: [_jsx(AdsEntity, { kind: "adaccount", id: q.data.adAccountId, name: q.data.name, label: findLabel(q.data.labels, 'adaccount', q.data.adAccountId) }), _jsxs("span", { className: "text-[var(--color-text-muted)]", children: [q.data.currency ?? '—', " \u00B7 ", q.data.timezoneName ?? '—'] }), _jsxs(Button, { type: "button", size: "sm", variant: "ghost", disabled: refresh.isPending, onClick: () => refresh.mutate(), className: "gap-1.5", children: [_jsx(RefreshCw, { "aria-hidden": true, className: "h-3.5 w-3.5" }), "\u041F\u0435\u0440\u0435\u0447\u0438\u0442\u0430\u0442\u044C \u0438\u0437 Meta"] })] }), _jsx(ProfileForm, { account: q.data }, `${q.data.profile?.updatedAt ?? 'new'}@${registry.data.version}`)] }));
}
function ProfileForm({ account }) {
    const { canManageAccounts } = useAdsLauncherPerms();
    const qc = useQueryClient();
    const { byKey } = useRegistryIndex();
    // «необязательно» — только при новом auto-ads (v4 §8.4); старый требует домен у пикселя
    const v4 = useAdsHealth().data?.features?.builderV4 === true;
    const assets = account.assets;
    const p = account.profile;
    // Поле есть, только если реестр auto-ads его включил: выключенное не
    // показывается вовсе — ни поля, ни обещаний (владелец: Threads вне задачи).
    const threadsEnabled = byKey.get('account.threadsUserId')?.enabled === true;
    // Аватары вариантов «Страница» / «Instagram»: что не пришло в `labels`
    // профиля — одним запросом на весь экран.
    const missingRefs = useMemo(() => [
        ...assets.pages.map((x) => ['page', x.id]),
        ...assets.instagramAccounts.map((x) => ['instagram', x.id]),
    ]
        .filter(([kind, id]) => findLabel(account.labels, kind, id) == null)
        .map(([kind, id]) => entityRef(kind, id)), [assets, account.labels]);
    const extra = useEntityLabels(account.platformAccountId, missingRefs);
    const assetLabelOf = useMemo(() => (kind, id) => findLabel(account.labels, kind, id) ?? extra.labelOf(kind, id), [account.labels, extra.labelOf]);
    const audienceOptions = assets.customAudiences.map((a) => ({
        value: a.id,
        label: [
            a.name ?? a.id,
            a.isValueBased ? 'value' : null,
            a.ratioPct != null ? `${a.ratioPct} %` : null,
            a.approximateCount != null ? `~${formatNumberRu(a.approximateCount)}` : null,
        ]
            .filter(Boolean)
            .join(' · '),
    }));
    // Аудитория слота, которой уже нет в кабинете (удалена или не попала в
    // список Meta): показывается в выборе как «нет в кабинете», чтобы было видно,
    // что сохранено, но сохранить профиль с ней нельзя — заменить или очистить.
    const audienceIds = new Set(audienceOptions.map((o) => o.value));
    const staleSlots = Object.entries(p?.audienceSlots ?? {}).filter(([, id]) => audienceOptions.length > 0 && id != null && !audienceIds.has(id));
    const staleIds = [...new Set(staleSlots.map(([, id]) => id))];
    // Имя пропавшей аудитории — из справочников auto-ads (`labels`), если знают.
    const staleName = (id) => findLabel(account.labels, 'audience', id)?.name ?? null;
    const staleOptions = staleIds.map((id) => {
        const name = staleName(id);
        return {
            value: id,
            label: name != null ? `«${name}» · ${id} — нет в кабинете` : `${id} — нет в кабинете`,
        };
    });
    const schema = useMemo(() => z
        .object({
        label: z.string().max(40).optional().meta({ label: 'Короткое имя (для названий)' }),
        pageId: idField(named('page', assets.pages), 'Страница', true),
        instagramUserId: idField(named('instagram', assets.instagramAccounts), 'Instagram', true, 'Обязателен: без него Meta подставит профиль страницы (PBIA).'),
        threadsUserId: idField([], byKey.get('account.threadsUserId')?.label ?? 'Threads', false),
        pixelId: idField(named('pixel', assets.pixels), 'Пиксель', true),
        // v4 §11.8: домен необязателен — пусто = объявления без домена конверсий
        conversionDomain: latinOnly(z.string().trim())
            .optional()
            .meta(v4
            ? {
                label: 'Домен конверсий (необязательно)',
                placeholder: 'witlab.top',
                description: CONVERSION_DOMAIN_HINT,
            }
            : { label: 'Домен конверсий' }),
        defaultLink: latinOnly(z
            .string()
            .trim()
            .regex(/^(https:\/\/.+)?$/, { message: 'Ссылка — только https://' }))
            .optional()
            .meta({ label: 'Ссылка по умолчанию', placeholder: 'https://' }),
        dsaPayor: latinOnly(z.string().trim()).optional().meta({ label: 'DSA: кто платит' }),
        dsaBeneficiary: latinOnly(z.string().trim())
            .optional()
            .meta({ label: 'DSA: в чьих интересах' }),
        slots: z
            .array(z.object({
            name: AdsAudienceSlotName.meta({
                label: 'Имя слота',
                placeholder: 'Например: LAL 1 % или Покупатели год',
                order: 1,
            }),
            // новая строка повторителя приходит пустой — «не выбрана»
            audienceId: (audienceOptions.length > 0
                ? z
                    .enum([NONE, ...audienceOptions.map((o) => o.value), ...staleIds], { message: STALE_AUDIENCE_MESSAGE })
                    .optional()
                : z
                    .string()
                    .regex(/^(\d+)?$/, { message: 'Id аудитории — только цифры' })
                    .optional()).meta({
                label: 'Аудитория кабинета',
                order: 2,
                options: [
                    { value: NONE, label: 'Не выбрана' },
                    ...audienceOptions,
                    ...staleOptions,
                ],
                ...(audienceOptions.length === 0 ? { placeholder: 'Id аудитории' } : {}),
            }),
        }))
            .max(ADS_AUDIENCE_SLOTS_MAX, { message: `Не больше ${ADS_AUDIENCE_SLOTS_MAX} слотов` })
            .superRefine((rows, ctx) => {
            const seen = new Set();
            rows.forEach((r, i) => {
                if (r.audienceId != null && staleIds.includes(r.audienceId)) {
                    ctx.addIssue({
                        code: 'custom',
                        message: STALE_AUDIENCE_MESSAGE,
                        path: [i, 'audienceId'],
                    });
                }
                if (seen.has(r.name)) {
                    ctx.addIssue({
                        code: 'custom',
                        message: 'Слот с таким именем уже есть',
                        path: [i, 'name'],
                    });
                }
                seen.add(r.name);
            });
        })
            .meta({
            label: 'Слоты аудиторий',
            description: 'Шаблон выбирает аудитории по имени слота, а здесь имя привязано к аудитории этого кабинета. Одно имя в разных кабинетах может вести на разные аудитории.',
            addLabel: 'Добавить слот',
            removeLabel: 'Удалить',
            itemLabel: 'Слот',
        }),
        regCategories: z
            .array(z.string().regex(/^[A-Z0-9_]+$/, {
            message: 'Категория Meta заглавными, например TAIWAN_UNIVERSAL',
        }))
            .max(30)
            .meta({
            label: 'Категории',
            placeholder: 'TAIWAN_UNIVERSAL и Enter',
            description: 'Как в Meta: TAIWAN_UNIVERSAL, SINGAPORE_UNIVERSAL, …',
        }),
        regIdentities: z
            .array(z.object({
            role: z
                .string()
                .regex(/^[a-z_]+$/, { message: 'Роль латиницей, например universal_payer' })
                .meta({ label: 'Роль', placeholder: 'universal_payer', order: 1 }),
            id: z
                .string()
                .regex(/^\d+$/, { message: 'Id идентичности — только цифры' })
                .meta({ label: 'Id идентичности', order: 2 }),
        }))
            .meta({
            label: 'Идентичности',
            description: 'Подтверждённые в Meta плательщик и бенефициар: роль → id.',
            addLabel: 'Добавить идентичность',
            removeLabel: 'Удалить',
            itemLabel: 'Идентичность',
        }),
        startTimeLocal: z
            .string()
            .regex(/^(([01]\d|2[0-3]):[0-5]\d)?$/, { message: 'Время — ЧЧ:ММ' })
            .optional()
            .meta({
            label: 'Час старта кабинета (время Лос-Анджелеса)',
            description: 'Для режима старта «Час кабинета», например 03:00.',
        }),
        enabled: z.boolean().meta({ label: 'Включён в конструкторе' }),
    })
        .superRefine((v, ctx) => {
        // регуляторные данные — либо целиком (категории + идентичности), либо ничего
        const cats = v.regCategories.length > 0;
        const ids = v.regIdentities.length > 0;
        if (cats && !ids) {
            ctx.addIssue({
                code: 'custom',
                message: 'Добавьте хотя бы одну идентичность — или уберите категории',
                path: ['regIdentities'],
            });
        }
        if (ids && !cats) {
            ctx.addIssue({
                code: 'custom',
                message: 'Добавьте хотя бы одну категорию — или уберите идентичности',
                path: ['regCategories'],
            });
        }
        const roles = new Set();
        v.regIdentities.forEach((r, i) => {
            if (roles.has(r.role)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Такая роль уже есть',
                    path: ['regIdentities', i, 'role'],
                });
            }
            roles.add(r.role);
        });
    }), [account, byKey, v4]);
    const defaults = {
        label: p?.label ?? '',
        pageId: p?.pageId ?? undefined,
        instagramUserId: p?.instagramUserId ?? undefined,
        threadsUserId: p?.threadsUserId ?? '',
        pixelId: p?.pixelId ?? undefined,
        conversionDomain: p?.conversionDomain ?? '',
        defaultLink: p?.defaultLink ?? '',
        dsaPayor: p?.dsaPayor ?? assets.account.defaultDsaPayor ?? '',
        dsaBeneficiary: p?.dsaBeneficiary ?? assets.account.defaultDsaBeneficiary ?? '',
        slots: Object.entries(p?.audienceSlots ?? {}).map(([name, id]) => ({
            name,
            audienceId: id ?? (audienceOptions.length > 0 ? NONE : ''),
        })),
        regCategories: p?.regionalRegulation?.categories ?? [],
        regIdentities: Object.entries(p?.regionalRegulation?.identities ?? {}).map(([role, id]) => ({ role, id })),
        startTimeLocal: p?.startTimeLocal ?? '',
        enabled: p?.enabled ?? true,
    };
    const save = useMutation({
        mutationFn: (input) => putAccountProfile(account.platformAccountId, input),
        onSuccess: () => {
            toast.success('Профиль кабинета сохранён');
            void qc.invalidateQueries({ queryKey: ADS_KEY });
        },
        onError: (err) => toast.error(describeAdsError(err, 'write')),
    });
    const orNull = (v) => (v && v.trim() !== '' && v !== NONE ? v.trim() : null);
    return (_jsxs(_Fragment, { children: [!account.readiness.ok ? (_jsxs(InlineNotice, { variant: "warning", bordered: true, as: "div", children: ["\u0417\u0430\u043F\u0443\u0441\u043A \u0432 \u044D\u0442\u043E\u0442 \u043A\u0430\u0431\u0438\u043D\u0435\u0442 \u043D\u0435\u0432\u043E\u0437\u043C\u043E\u0436\u0435\u043D: ", notReadyText(account, byKey), "."] })) : null, staleSlots.length > 0 ? (_jsxs(InlineNotice, { variant: "warning", bordered: true, as: "div", "data-testid": "stale-audiences", children: [staleSlots.length === 1 ? 'В слоте ' : 'В слотах ', staleSlots.map(([name]) => `«${name}»`).join(', '), " \u0432\u044B\u0431\u0440\u0430\u043D\u0430 \u0430\u0443\u0434\u0438\u0442\u043E\u0440\u0438\u044F, \u043A\u043E\u0442\u043E\u0440\u043E\u0439 \u0431\u043E\u043B\u044C\u0448\u0435 \u043D\u0435\u0442 \u0432 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0435. \u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0434\u0440\u0443\u0433\u0443\u044E \u0438\u043B\u0438 \u00AB\u041D\u0435 \u0432\u044B\u0431\u0440\u0430\u043D\u0430\u00BB \u2014 \u0438\u043D\u0430\u0447\u0435 \u043F\u0440\u043E\u0444\u0438\u043B\u044C \u043D\u0435 \u0441\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C.", _jsx("span", { className: "mt-1 flex flex-wrap gap-x-3 gap-y-1", children: staleIds.map((id) => (_jsx(AdsEntity, { kind: "audience", id: id, label: findLabel(account.labels, 'audience', id), variant: "inline" }, id))) })] })) : null, !canManageAccounts ? (_jsx(InlineNotice, { variant: "muted", as: "div", children: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430 \u043C\u0435\u043D\u044F\u0435\u0442 \u0442\u043E\u043B\u044C\u043A\u043E \u0432\u043B\u0430\u0434\u0435\u043B\u0435\u0446 \u2014 \u0437\u0434\u0435\u0441\u044C \u043F\u0440\u043E\u0441\u043C\u043E\u0442\u0440." })) : null, _jsxs(ZodForm, { schema: schema, defaultValues: defaults, readonly: !canManageAccounts, className: "flex flex-col gap-4", onSubmit: (raw) => {
                    const v = raw;
                    const input = {
                        ...(v.label?.trim() ? { label: v.label.trim() } : {}),
                        pageId: v.pageId,
                        instagramUserId: v.instagramUserId,
                        ...(threadsEnabled
                            ? { threadsUserId: orNull(v.threadsUserId) }
                            : {}),
                        pixelId: v.pixelId,
                        conversionDomain: orNull(v.conversionDomain),
                        defaultLink: orNull(v.defaultLink),
                        dsaPayor: orNull(v.dsaPayor),
                        dsaBeneficiary: orNull(v.dsaBeneficiary),
                        audienceSlots: Object.fromEntries(v.slots.map((r) => [r.name.trim(), orNull(r.audienceId)])),
                        regionalRegulation: v.regCategories.length > 0 && v.regIdentities.length > 0
                            ? {
                                categories: v.regCategories,
                                identities: Object.fromEntries(v.regIdentities.map((r) => [r.role, r.id])),
                            }
                            : null,
                        startTimeLocal: orNull(v.startTimeLocal),
                        enabled: v.enabled,
                    };
                    return submitting(save.mutateAsync(input));
                }, children: [_jsx(SectionCard, { title: "\u041E\u0431\u044A\u0435\u043A\u0442\u044B Meta", children: _jsx(AssetLabelsContext.Provider, { value: assetLabelOf, children: _jsxs("div", { className: "flex flex-col gap-4", children: [_jsx(ZodField, { name: "pageId" }), _jsx(ZodField, { name: "instagramUserId" }), threadsEnabled ? _jsx(ZodField, { name: "threadsUserId" }) : null, _jsx(ZodField, { name: "pixelId" }), _jsx(ZodField, { name: "conversionDomain" }), _jsx(ZodField, { name: "defaultLink" })] }) }) }), _jsx(SectionCard, { title: "DSA (\u0415\u0421)", children: _jsxs("div", { className: "grid gap-4 sm:grid-cols-2", children: [_jsx(ZodField, { name: "dsaPayor" }), _jsx(ZodField, { name: "dsaBeneficiary" })] }) }), _jsx(SectionCard, { title: "\u0421\u043B\u043E\u0442\u044B \u0430\u0443\u0434\u0438\u0442\u043E\u0440\u0438\u0439", children: _jsx(ZodField, { name: "slots" }) }), _jsx(SectionCard, { title: "\u0420\u0435\u0433\u0438\u043E\u043D\u0430\u043B\u044C\u043D\u044B\u0435 \u0440\u0435\u0433\u0443\u043B\u044F\u0442\u043E\u0440\u043D\u044B\u0435 \u0434\u0430\u043D\u043D\u044B\u0435", hint: _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u0422\u0430\u0439\u0432\u0430\u043D\u044C, \u0421\u0438\u043D\u0433\u0430\u043F\u0443\u0440 \u0438 \u0434\u0440.: \u0443\u0445\u043E\u0434\u044F\u0442 \u0432 \u043A\u0430\u0436\u0434\u0443\u044E \u0433\u0440\u0443\u043F\u043F\u0443. \u041F\u0443\u0441\u0442\u043E \u2014 \u043D\u0435 \u043E\u0442\u043F\u0440\u0430\u0432\u043B\u044F\u044E\u0442\u0441\u044F." }), children: _jsxs("div", { className: "flex flex-col gap-4", children: [_jsx(ZodField, { name: "regCategories" }), _jsx(ZodField, { name: "regIdentities" })] }) }), _jsx(SectionCard, { title: "\u0421\u0442\u0430\u0440\u0442 \u0438 \u0443\u0447\u0430\u0441\u0442\u0438\u0435", children: _jsxs("div", { className: "flex flex-col gap-4", children: [_jsx(ZodField, { name: "label" }), _jsx(ZodField, { name: "startTimeLocal", placeholder: "03:00" }), _jsx(ZodField, { name: "enabled" })] }) }), canManageAccounts ? (_jsx(Actions, { children: _jsx(SubmitButton, { pendingLabel: "\u0421\u043E\u0445\u0440\u0430\u043D\u044F\u0435\u043C\u2026", children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u043F\u0440\u043E\u0444\u0438\u043B\u044C" }) })) : null] })] }));
}
//# sourceMappingURL=account.js.map